import React from 'react';
import { isSafeReferenceUrl, ReferenceCard, AuthorAvatar } from './campaignDetailsParts';
import { FIELD_LABELS, formatRelativeTime, type ChangeSummary } from './editRequestFormat';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Export, LinkSimple, DotsThreeVertical, PencilSimple, Trash, ArrowRight } from '../../design-system/icons';

// ── Comments sheet card ───────────────────────────────────────────────────

const CHANGE_BADGE: Record<ChangeSummary['kind'], string> = {
  changed: 'bg-amber-100 text-amber-700',
  added: 'bg-violet-100 text-violet-700',
  removed: 'bg-rose-100 text-rose-700',
};

interface CommentCardProps {
  path: string;
  change: ChangeSummary;
  comment: string;
  /** ISO stamp of when the note was written; absent until there is one. */
  commentedAt?: string;
  authorName: string;
  editing: boolean;
  onEdit: () => void;
  onEditDone: () => void;
  onCommentChange: (value: string) => void;
  onGoToField: () => void;
}

/**
 * One noted change in the Comments sheet: what kind of edit it was, the old→new
 * values in the same formatting the campaign screens use, who wrote the note
 * and when, and a jump link back to the field. The note itself is editable and
 * removable from the ⋮ menu — removing it drops the card, since the sheet only
 * lists changes that carry a note.
 */
export function CommentCard({
  path,
  change,
  comment,
  commentedAt,
  authorName,
  editing,
  onEdit,
  onEditDone,
  onCommentChange,
  onGoToField,
}: CommentCardProps) {
  const [draft, setDraft] = React.useState(comment);

  const startEdit = () => {
    setDraft(comment);
    onEdit();
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="space-y-2.5 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-sm-medium text-foreground">{FIELD_LABELS[path] ?? path}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase ${CHANGE_BADGE[change.kind]}`}>
            {change.kind}
          </span>
        </div>
        {!editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Note options for ${FIELD_LABELS[path] ?? path}`}
                className="-mr-1 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <DotsThreeVertical size={16} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={startEdit}>
                <PencilSimple size={14} />
                Edit comment
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onSelect={() => onCommentChange('')}>
                <Trash size={14} />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="text-body-sm-regular text-muted-foreground">
        {change.kind === 'changed' ? (
          <>
            <span className="line-through">{change.before}</span>
            {' → '}
            <span className="font-medium text-foreground">{change.after}</span>
          </>
        ) : (
          <>
            <span className="capitalize">{change.kind}</span>
            {' → '}
            <span className="font-medium text-foreground">{change.after}</span>
          </>
        )}
      </div>

      {editing ? (
        <div className="space-y-2 border-t border-border pt-2.5">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a comment"
            rows={3}
            className="field-sizing-fixed min-h-0 resize-none"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onEditDone}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={draft === comment}
              onClick={() => {
                onCommentChange(draft);
                onEditDone();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 border-t border-border pt-2.5">
          <AuthorAvatar name={authorName} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2 text-caption-regular text-muted-foreground">
              <span className="truncate font-medium text-foreground">{authorName}</span>
              {commentedAt && <span className="shrink-0">{formatRelativeTime(commentedAt)}</span>}
            </div>
            <p className="text-body-sm-regular wrap-break-word text-foreground">{comment}</p>
          </div>
        </div>
      )}
      </div>

      <button
        type="button"
        onClick={onGoToField}
        className="flex w-full items-center justify-center gap-1.5 border-t border-border bg-muted py-3 text-body-sm-medium text-foreground transition-colors hover:bg-accent"
      >
        Go to field <ArrowRight size={16} weight="bold" />
      </button>
    </div>
  );
}

// ── Segmented control ─────────────────────────────────────────────────────
// Single-select pill bar with a sliding active indicator (Figma "Segmented
// Control"). Segments are equal-width (flex-1) so the indicator — one segment
// wide — can translate by whole-segment steps as the selection changes.
export function SegmentedControl({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const activeIndex = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div className="relative flex w-full rounded-full border border-border bg-card p-1">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-1 left-1 rounded-full bg-foreground transition-transform duration-200 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${options.length})`, transform: `translateX(${activeIndex * 100}%)` }}
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`relative z-10 flex-1 rounded-full px-3 py-1.5 text-center text-body-sm-medium transition-colors ${
            value === o.value ? 'text-background' : 'text-foreground hover:text-foreground/70'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── References ────────────────────────────────────────────────────────────
// Multiple reference videos are stored in the single `brief.sample_video_url`
// string as a comma-separated list (the BE field is one string — no schema
// change). This field parses that list, renders each as a thumbnail card with
// a play affordance + remove, and supports pasting more links one at a time.
export function ReferencesField({
  value,
  onChange,
  labelExtra,
}: {
  value: string;
  onChange: (value: string) => void;
  labelExtra?: React.ReactNode;
}) {
  const urls = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const [input, setInput] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const commit = () => {
    const u = input.trim();
    if (!u || urls.includes(u)) {
      setInput('');
      setError(null);
      return;
    }
    if (!isSafeReferenceUrl(u)) {
      setError('Only http:// and https:// links are allowed.');
      return;
    }
    onChange([...urls, u].join(', '));
    setInput('');
    setError(null);
  };
  const removeAt = (i: number) => onChange(urls.filter((_, idx) => idx !== i).join(', '));

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-body-sm-medium text-foreground">
          <Export size={15} className="shrink-0" />
          References
          {labelExtra}
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-caption-regular text-muted-foreground">Optional</span>
      </div>
      <p className="mb-3 text-caption-regular text-muted-foreground">
        Mood boards or example videos creators can study.
      </p>

      {/* Paste-link only: there's no upload backend yet, so the tab pair is
          left out rather than offering a dead "Upload file" option. */}
      <div className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-body-sm-medium text-foreground">
        <LinkSimple size={14} />
        Paste link
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          placeholder="https://youtube.com/watch?v=… or Instagram URL"
        />
        <Button type="button" size="sm" onClick={commit} disabled={!input.trim()}>
          Add
        </Button>
      </div>
      {error ? (
        <p className="mt-1.5 text-caption-regular text-rose-600">{error}</p>
      ) : (
        <p className="mt-1.5 text-caption-regular text-muted-foreground">
          Paste YouTube, Instagram, TikTok, or Vimeo links. Multiple supported.
        </p>
      )}

      {urls.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {urls.map((u, i) => (
            <ReferenceCard key={u} url={u} onRemove={() => removeAt(i)} />
          ))}
        </div>
      )}
    </div>
  );
}
